import { useState, useEffect, useCallback, useMemo } from 'react';
import { getAssetCategory } from '../utils/hip3Assets';

export const FILTER_DEFAULTS = {
  whaleTradesMinVolume: 200000,    // $200K
  liquidationsMinVolume: 50000,    // $50K
  selectedCoins: [],               // Empty = all coins
  selectedCategories: []             // empty = all, or array of: 'crypto', 'stock', 'commodity', 'index', 'forex', 'etf'
};

/**
 * Custom hook for trade filters management
 * Based on js/filters.js
 */
export function useFilters() {

  const [filters, setFilters] = useState(() => {
    // Load from localStorage or use defaults
    try {
      const saved = localStorage.getItem('tradeFilterPreferences');
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          whaleTradesMinVolume: parsed.whaleTradesMinVolume || FILTER_DEFAULTS.whaleTradesMinVolume,
          liquidationsMinVolume: parsed.liquidationsMinVolume || FILTER_DEFAULTS.liquidationsMinVolume,
          selectedCoins: parsed.selectedCoins || FILTER_DEFAULTS.selectedCoins,
          selectedCategories: parsed.selectedCategories || FILTER_DEFAULTS.selectedCategories
        };
      }
    } catch (error) {
      console.error('❌ Error loading filters from localStorage:', error);
    }

    // Check URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const volumeParam = urlParams.get('volume');

    if (volumeParam) {
      const volumeValue = parseInt(volumeParam);
      if (!isNaN(volumeValue) && volumeValue > 0) {
        return {
          ...FILTER_DEFAULTS,
          whaleTradesMinVolume: volumeValue
        };
      }
    }

    return FILTER_DEFAULTS;
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem('tradeFilterPreferences', JSON.stringify(filters));
      console.log('💾 Saved filter preferences:', filters);
    } catch (error) {
      console.error('❌ Error saving filters to localStorage:', error);
    }
  }, [filters]);

  // Update whale trades minimum volume
  const setWhaleTradesMinVolume = useCallback((value) => {
    setFilters(prev => ({
      ...prev,
      whaleTradesMinVolume: Math.max(0, value)
    }));
  }, []);

  // Update liquidations minimum volume
  const setLiquidationsMinVolume = useCallback((value) => {
    setFilters(prev => ({
      ...prev,
      liquidationsMinVolume: Math.max(0, value)
    }));
  }, []);

  // Update selected coins
  const setSelectedCoins = useCallback((coins) => {
    setFilters(prev => ({
      ...prev,
      selectedCoins: Array.isArray(coins) ? coins : []
    }));
  }, []);

  // Toggle asset category (multi-select)
  const toggleCategory = useCallback((category) => {
    setFilters(prev => {
      const current = prev.selectedCategories || [];
      const has = current.includes(category);
      const next = has ? current.filter(c => c !== category) : [...current, category];
      return { ...prev, selectedCategories: next };
    });
  }, []);

  // Clear all category selections (show all)
  const clearCategories = useCallback(() => {
    setFilters(prev => ({ ...prev, selectedCategories: [] }));
  }, []);

  // Check if a trade should be displayed
  const shouldDisplayTrade = useCallback((trade) => {
    // Volume filter
    if (trade.isLiquidation) {
      if (trade.dollarSize < filters.liquidationsMinVolume) return false;
    } else {
      if (trade.dollarSize < filters.whaleTradesMinVolume) return false;
    }

    // Category filter (multi-select)
    const cats = filters.selectedCategories;
    if (cats && cats.length > 0) {
      const coin = trade.baseCoin || trade.symbol?.replace(/USDT$|USD$/i, '') || '';
      const tradeCategory = getAssetCategory(coin);
      if (!cats.includes(tradeCategory)) return false;
    }

    return true;
  }, [filters]);

  // Flag: true when saved filters are >5x stricter than defaults
  const isFilterStrictFromStorage = useMemo(() => {
    return filters.whaleTradesMinVolume > FILTER_DEFAULTS.whaleTradesMinVolume * 5 ||
           filters.liquidationsMinVolume > FILTER_DEFAULTS.liquidationsMinVolume * 5;
  }, [filters.whaleTradesMinVolume, filters.liquidationsMinVolume]);

  // Reset to defaults
  const resetToDefaults = useCallback(() => {
    setFilters(FILTER_DEFAULTS);
    console.log('🔄 Reset filters to defaults');
  }, []);

  // Format dollar amount for display
  const formatDollarAmount = useCallback((value) => {
    if (value >= 1e6) {
      return '$' + (value / 1e6).toFixed(1) + 'M';
    } else if (value >= 1e3) {
      return '$' + (value / 1e3).toFixed(0) + 'K';
    }
    return '$' + value.toFixed(0);
  }, []);

  // Get summary
  const getSummary = useCallback(() => {
    return {
      whaleTrades: formatDollarAmount(filters.whaleTradesMinVolume),
      liquidations: formatDollarAmount(filters.liquidationsMinVolume)
    };
  }, [filters, formatDollarAmount]);

  return {
    filters,
    setWhaleTradesMinVolume,
    setLiquidationsMinVolume,
    setSelectedCoins,
    toggleCategory,
    clearCategories,
    shouldDisplayTrade,
    resetToDefaults,
    isFilterStrictFromStorage,
    getSummary,
    formatDollarAmount
  };
}
